import React, { useMemo, useState } from 'react';
import { debounce, filter, find, forEach, get, groupBy, isEmpty, map, trim, values } from 'lodash';
import { styled } from '@mui/system';
import { AccordionDetails } from '@mui/material';
import ArrowIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import UIUtils from '../../../lib/UIUtils';
import DebouncedText from '../../elements/editors/mui/DebouncedText';
import { getCurrentSelectedOption } from '../../elements/rselect/ReactSelectUtils';
import AuthenticateDataSource from './AuthenticateDataSource';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import { MetaInfoInputForm } from 'new/components/MetaInfoInputForm/MetaInfoInputForm';
import {
  FieldBuilderRedesigned,
  HeaderField,
} from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import styles from './ConfigureTemplatizedScript.module.scss';
import { NavScroller, NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import classnames from 'classnames';
import { Box } from 'new/elements/Box';
import MonitoringInterval from 'js/components/data_sources/add_ds_form/MonitoringInterval/MonitoringInterval';
import Tooltip from '../../elements/mui/Tooltip';
import { CONNECTOR_CODE_SQL_RUNNER } from 'js/constants/connectionCategories';
import { getSourceEndpointOptions } from 'new/utils/actionEndpoints';

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square children={null} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': { borderBottom: 0 },
  '&::before': { display: 'none' },
}));

const Icon = <ArrowIcon sx={{ fontSize: '1rem' }} strick="red" />;
const AccordionSummary = styled((props) => <MuiAccordionSummary expandIcon={Icon} {...props} />)(
  () => {
    return {
      padding: 0,
      paddingLeft: 8,
      border: 'none',
      borderRadius: 8,
      backgroundColor: 'var(--color-primary-50)',
      flexDirection: 'row-reverse',
      '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
      },
      '& .MuiAccordionSummary-content': {
        marginLeft: 12,
      },
    };
  },
);

const navOptions = [
  { label: 'Configuration', id: 'section-configuration' },
  { label: 'Schedule Ingestion', id: 'section-scheduling' },
];

const CredentialField = (props) => {
  const {
    paging: { credentialsAllowed, metaInfoById, connAuthById, connAuthMeta },
    vendors,
    credentials,
    credentialsByType,
    credentialsById,
    credentialsData,
    templatizedScriptConfig,
    isLoadingCredentials,
    loadCredentials,
    onScriptCredentialInputChanged,
    onValidateNewScriptCredential,
    field,
    disabled,
  } = props;

  const { name, description, display_name, credential_type } = field;
  const { templates, items } = credential_type ? connAuthById[credential_type] : null;

  const [selectedTemplate, setSelectedTemplate] = useState(templates?.[0]?.id);

  const onChangeTemplate = (id) => setSelectedTemplate(Number(id));

  const connectionAuthFields = useMemo(() => {
    const template = templates?.find((t) => t.id === selectedTemplate) || templates?.[0];
    return templates && templates?.length > 1 && template
      ? items.filter((item) => template.auth_parameters.includes(item.param_id))
      : items;
  }, [templates, items, selectedTemplate]);

  const connectionAuthMeta = credential_type ? connAuthMeta[credential_type] : null;

  const selectedCredentialId = templatizedScriptConfig[name];
  const selectedCredential = selectedCredentialId && credentials[selectedCredentialId];
  const credentialData = {
    ...(credentialsData[name] || {}),
    selectedCredentialId: selectedCredentialId || null,
  };

  return (
    <div className="accordion-group" style={{ width: '100%' }}>
      <Accordion square defaultExpanded>
        <AccordionSummary className={styles.color_svg}>
          <div className={styles.header_accordion}>
            <h2 className={styles.accordion_title}>{display_name}</h2>
            <span className="accordion-content-grow iteration-info">
              {selectedCredential && selectedCredential.name}
            </span>
          </div>
        </AccordionSummary>
        <AccordionDetails className="accordion-body">
          <div className={styles.credentialBodyContent}>
            {description && <p className="credential-field-description">{description}</p>}
            <div className={disabled ? styles.credentialDisabledWrapper : null}>
              <AuthenticateDataSource
                onChangeApiType={props.onChangeApiType}
                formData={credentialData}
                connectionTypes={credentialsAllowed}
                connectionMetas={metaInfoById}
                vendors={vendors}
                selectedConnection={credential_type}
                templates={templates}
                selectedTemplate={selectedTemplate}
                onChangeTemplate={onChangeTemplate}
                connectionAuthFields={connectionAuthFields}
                connectionAuthMeta={connectionAuthMeta}
                dataCredentials={credentialsByType}
                credentialsById={credentialsById}
                isFormAwaitingValidation={false}
                isLoadingData={isLoadingCredentials}
                onRefreshClicked={loadCredentials}
                onFormInputChanged={(...args) => onScriptCredentialInputChanged(name, ...args)}
                onCredSelectionChanged={() => {}}
                nextFormStage={(...args) => onValidateNewScriptCredential(name, ...args)}
              />
              {disabled && (
                <Tooltip
                  title="Credential cannot be changed when editing this source."
                  placement="top"
                >
                  <div className={styles.credentialDisabledOverlay} aria-hidden />
                </Tooltip>
              )}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

class ConfigureTemplatizedScript extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTemplate: null,
    };

    this._getEditorInputField = this._getEditorInputField.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._getSelectField = this._getSelectField.bind(this);
  }

  componentDidMount() {
    const {
      paging: { sourceConfigById },
      selectedConnection,
      loadDataMaps,
    } = this.props;
    const sourceConfigFields = sourceConfigById[selectedConnection];

    if (find(sourceConfigFields, { type: 'data_map' })) {
      loadDataMaps(false, ACLQueryModeTypes.MODE_COLLABORATOR.key);
    }
  }

  render() {
    const {
      paging: { sourceConfigById, metaInfoById },
      dataMaps,
      vendorEndpoints,
      selectedConnection,
      templatizedScriptConfig,
      name = '',
      description = '',
      onMetaInfoChanged,
      visibleFieldData,
      credentialsData,
      sourceBeingEdited,
      vendors,
      flowType,
      isAPIServerTarget,
    } = this.props;

    const vendor = vendors?.[sourceBeingEdited?.vendor_id];
    const isEditingEltScriptRunner = Boolean(vendor && vendor?.name === CONNECTOR_CODE_SQL_RUNNER);
    const sourceConfigFields = sourceConfigById[selectedConnection];
    const fieldsByIsEndpoint = groupBy(
      sourceConfigFields,
      (field) => field.name === 'vendor_endpoint_id',
    );
    const endpointField = (fieldsByIsEndpoint[true] && fieldsByIsEndpoint[true][0]) || {};
    const otherFields = fieldsByIsEndpoint[false];
    const visibleFields = filter(
      otherFields,
      (field) =>
        UIUtils.shouldRenderField(field, templatizedScriptConfig) &&
        UIUtils.shouldHideField(field, templatizedScriptConfig) === false,
    );
    if (!endpointField.options) return null;
    // Action endpoints are only offered for API Server targets; ETL sources hide them, except the
    // endpoint an edited source already uses (or has selected).
    const endpointOptions = getSourceEndpointOptions(endpointField.options, {
      flowType,
      isAPIServerTarget,
      sourceBeingEdited,
      selectedEndpointId: templatizedScriptConfig?.vendor_endpoint_id,
    });

    let dataMapOptions;
    let dataMapColumnOptions = {};

    if (find(visibleFields, { type: 'data_map' })) {
      dataMapOptions = map(values(dataMaps), (value) => ({
        label: value.name,
        value: value.id,
      }));

      const dataMapColumnFields = filter(visibleFields, {
        type: 'data_map_column',
      });

      forEach(dataMapColumnFields, (field) => {
        const dataMapId = templatizedScriptConfig[field.dataMapField];
        const dataMap = dataMapId && dataMaps[dataMapId];

        if (dataMap) {
          dataMapColumnOptions[field.name] = map(
            dataMap.map_entry_schema.properties,
            (def, name) => ({
              label: name,
              value: name,
            }),
          );
        }
      });
    }

    const selectedEndpointId = templatizedScriptConfig?.vendor_endpoint_id;
    const selectedEndpoint = selectedEndpointId && vendorEndpoints[selectedEndpointId];
    const meta = selectedConnection && metaInfoById[selectedConnection];
    const connectionNameLabel = (meta && meta.displayName) || 'Script';
    const connectionIcon = meta.iconSm ? (
      <img src={meta.iconSm} alt={connectionNameLabel} height={18} />
    ) : null;

    const fieldWithoutFrequencyField = visibleFields.filter((field) => field.type !== 'frequency');
    const frequencyField = visibleFields.find((field) => field.type === 'frequency');

    const frequencyValue = frequencyField ? templatizedScriptConfig[frequencyField?.name] : null;

    return (
      <FormConfigurationStep
        connectionName={connectionNameLabel}
        connectionIcon={connectionIcon}
        formContainerClassname={styles.form_container}
        headerChildren={
          <MetaInfoInputForm name={name} description={description} onChange={onMetaInfoChanged} />
        }
      >
        <NavScroller navOptions={navOptions}>
          <NavScrollerContext.Consumer>
            {(consumer) => {
              return (
                <div className="nav-scroller-container">
                  <div id="section-configuration" className={styles.configuration_container}>
                    <div className={styles.connection_inputs_container}>
                      <Box
                        title="Configuration"
                        className={classnames(styles.configuration_box, {
                          [styles.configuration_focused]:
                            consumer?.activeScroll === 'section-configuration',
                        })}
                      >
                        <ol>
                          {endpointOptions.length !== 1 && (
                            <li className={styles.configuration_section_container}>
                              <div>
                                <label>Endpoint</label>
                                {this._getSelectField(
                                  {
                                    name: 'vendor_endpoint_id',
                                    inputType: 'select',
                                    unsorted: true,
                                    options: endpointOptions,
                                  },
                                  endpointOptions,
                                )}
                              </div>

                              {selectedEndpoint &&
                                (selectedEndpoint.description ||
                                  selectedEndpoint.config.info_url) && (
                                  <div className="data-path-suggestions">
                                    <h4>Details</h4>
                                    <div className="suggestions-container endpoint-info-container">
                                      {selectedEndpoint.description && (
                                        <div>
                                          {UIUtils.stringToParagraphs(selectedEndpoint.description)}
                                        </div>
                                      )}
                                      {selectedEndpoint.config.info_url && (
                                        <a
                                          className="endpoint-doc-link undecorated-link"
                                          href={selectedEndpoint.config.info_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Open Documentation
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                )}
                            </li>
                          )}

                          {!isEmpty(fieldWithoutFrequencyField) &&
                            map(fieldWithoutFrequencyField, (field) => {
                              let fieldComponent;
                              const { name, type } = field;

                              const wrappedLabelField = (
                                <HeaderField
                                  item={field}
                                  showRequiredIndicator
                                  className={styles.input_label}
                                />
                              );

                              if (!isEmpty(field.options)) {
                                const isStorageTypeDisabled =
                                  isEditingEltScriptRunner && name === 'parameters.storage_type';
                                fieldComponent = this._getSelectField(field, field.options, {
                                  disabled: isStorageTypeDisabled,
                                });
                                if (isStorageTypeDisabled) {
                                  fieldComponent = (
                                    <Tooltip
                                      title="Database type cannot be changed when editing this source."
                                      placement="top"
                                    >
                                      <span className={styles.tooltipWrapper}>
                                        {fieldComponent}
                                      </span>
                                    </Tooltip>
                                  );
                                }
                              } else if (type === 'data_map') {
                                fieldComponent = this._getSelectField(field, dataMapOptions);
                              } else if (type === 'data_map_column') {
                                fieldComponent = this._getSelectField(
                                  field,
                                  dataMapColumnOptions[name] || [],
                                );
                              } else if (type === 'script_credential') {
                                fieldComponent = (
                                  <CredentialField {...this.props} field={field} disabled={false} />
                                );
                              } else if (type === 'sql_editor') {
                                fieldComponent = (
                                  <div className={styles.configuration_section_container}>
                                    {wrappedLabelField}
                                    {this._getEditorInputField(name, {})}
                                  </div>
                                );
                              } else if (
                                type === 'boolean' ||
                                field.inputType === 'checkbox' ||
                                field.inputType === 'boolean'
                              ) {
                                fieldComponent = this._getBooleanCheckboxField(field);
                              } else {
                                fieldComponent = (
                                  <div className={styles.configuration_section_container}>
                                    {wrappedLabelField}
                                    {this._getTextField(
                                      name,
                                      field.inputType === 'textarea'
                                        ? {
                                            multiline: true,
                                            minRows: 4,
                                            maxRows: 8,
                                          }
                                        : {},
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <div aria-label="field-component" key={name}>
                                  {fieldComponent}
                                </div>
                              );
                            })}
                        </ol>
                      </Box>
                    </div>
                  </div>

                  <div
                    id="section-scheduling"
                    className={classnames({
                      [styles.monitor_interval_container]: true,
                      [styles.frequency_field_focused]:
                        consumer?.activeScroll === 'section-scheduling',
                    })}
                  >
                    {Boolean(frequencyField) ? (
                      <MonitoringInterval
                        label="Scheduling"
                        source={this.props.sourceBeingEdited}
                        value={frequencyValue}
                        config={visibleFieldData}
                        field={frequencyField}
                        onConfigChanged={(data, field, value) => {
                          this.props.onTemplatizedScriptConfigChanged(data, field, value);
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              );
            }}
          </NavScrollerContext.Consumer>
        </NavScroller>
      </FormConfigurationStep>
    );
  }

  _getEditorInputField(name) {
    const { templatizedScriptConfig, onTemplatizedScriptConfigChanged } = this.props;

    return (
      <div className={styles.editor_container}>
        <LoadableMonacoEditor
          language="sql"
          value={get(templatizedScriptConfig, name, '')}
          onChange={debounce(
            (text) => onTemplatizedScriptConfigChanged(templatizedScriptConfig, name, text),
            200,
          )}
          theme="dark"
          height={250}
          options={{
            minimap: { enabled: false },
          }}
        />
      </div>
    );
  }

  _getTextField(name, props = {}) {
    const { templatizedScriptConfig, onTemplatizedScriptConfigChanged } = this.props;

    return (
      <DebouncedText
        id={name}
        value={get(templatizedScriptConfig, name, '')}
        onChange={(text) =>
          onTemplatizedScriptConfigChanged(templatizedScriptConfig, name, trim(text))
        }
        {...props}
      />
    );
  }

  _getBooleanCheckboxField(field) {
    const { templatizedScriptConfig, onTemplatizedScriptConfigChanged } = this.props;

    const { name = '', required = false } = field;
    const raw = get(templatizedScriptConfig, name);
    const defaultVal =
      field.default !== undefined
        ? field.default
        : field.defaultValue !== undefined
          ? field.defaultValue
          : false;

    const currentFieldValue = raw !== undefined && raw !== null && raw !== '' ? raw : defaultVal;

    return (
      <div className={styles.configuration_section_container}>
        <FieldBuilderRedesigned
          field={field}
          id={field.name}
          isRequired={required}
          showRequiredIndicator
          fieldLabelClassName={styles.input_label}
          currentFieldValue={currentFieldValue}
          onChange={(fieldName, value) => {
            onTemplatizedScriptConfigChanged(templatizedScriptConfig, fieldName, value);
          }}
        />
      </div>
    );
  }

  _getSelectField(field, options, extraProps = {}) {
    const { templatizedScriptConfig, onTemplatizedScriptConfigChanged } = this.props;

    const { name = '', required = false } = field;
    const currentValue = getCurrentSelectedOption(options, templatizedScriptConfig[name]);

    return (
      <div className={styles.configuration_section_container}>
        <FieldBuilderRedesigned
          field={field}
          id={field.name}
          isRequired={required}
          showRequiredIndicator
          fieldLabelClassName={styles.input_label}
          currentFieldValue={currentValue}
          onChange={(name, value) => {
            onTemplatizedScriptConfigChanged(templatizedScriptConfig, name, value);
          }}
          {...extraProps}
        />
      </div>
    );
  }
}

export default ConfigureTemplatizedScript;
